import type { CarrierPrice } from "~/shared/schemas/carrier-schema";
import { getIsInternal } from "~/shared/utils/get-deployment-type.ts";

const liabilityFormatter = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	minimumFractionDigits: 0,
	maximumFractionDigits: 0,
});

const normalizeLiabilityValue = (value: number | null | undefined): number => {
	return typeof value === "number" && Number.isFinite(value) ? value : 0;
};

const formatLiabilityValue = (value: number | null | undefined): string => {
	const numericValue = normalizeLiabilityValue(value);
	return liabilityFormatter.format(numericValue);
};

/**
 * Gets the carrier logo URL for a given SCAC (Standard Carrier Alpha Code).
 * Falls back to a default logo if no SCAC is provided.
 */
export const getCarrierLogoUrl = (
	scac: string | null | undefined,
	baseUrl: string,
): string => {
	if (scac) {
		return `${baseUrl}/images/carrier-logos/${scac}.jpg`;
	}
	return `${baseUrl}/images/carrier-logos/logo-default.png`;
};

export const getUniqueQuoteIds = (
	carrierPrices: CarrierPrice[],
): (string | number)[] => {
	return [
		"",
		...carrierPrices
			.map((carrier) => carrier.quoteId)
			.filter((value, index, self) => self.indexOf(value) === index),
	];
};

export type ServiceLevelOption = {
	id: number;
	name: string;
};

const getServiceLevelForCarrierPrice = (
	carrierPrice: CarrierPrice,
): ServiceLevelOption | null => {
	const isExternal = !getIsInternal();

	// For external users, check carrier flags first
	if (isExternal) {
		if (carrierPrice.carrier.isEconomy) return { id: 4, name: "Economy" };
		if (
			carrierPrice.carrier.isPreferred ||
			carrierPrice.carrier.isCarrierOfTheYear
		)
			return { id: 3, name: "Preferred" };
	}

	// Map based on serviceLevelId
	const { serviceLevelId } = carrierPrice;
	if (serviceLevelId === 1 || serviceLevelId === 8)
		return { id: 1, name: "Standard" };
	if (serviceLevelId === 7) return { id: 5, name: "Volume" };
	if (serviceLevelId > 1 && serviceLevelId < 7)
		return { id: 2, name: "Guaranteed" };

	return null;
};

export const getUniqueServiceLevels = (
	carrierPrices: CarrierPrice[],
): string[] => {
	const serviceLevelMap = new Map<number, string>();

	for (const carrierPrice of carrierPrices) {
		const serviceLevel = getServiceLevelForCarrierPrice(carrierPrice);
		if (serviceLevel && !serviceLevelMap.has(serviceLevel.id)) {
			serviceLevelMap.set(serviceLevel.id, serviceLevel.name);
		}
	}

	return [...serviceLevelMap.values()];
};

export const matchesServiceLevelFilter = (
	carrierPrice: CarrierPrice,
	selectedServiceLevels: string[],
): boolean => {
	// Independent checks matching legacy behavior — a carrier can match multiple filters
	return (
		(selectedServiceLevels.includes("Standard") &&
			carrierPrice.serviceLevelId === 1) ||
		(selectedServiceLevels.includes("Guaranteed") &&
			carrierPrice.serviceLevelId >= 2 &&
			carrierPrice.serviceLevelId <= 6) ||
		(selectedServiceLevels.includes("Preferred") &&
			(carrierPrice.carrier.isPreferred === true ||
				carrierPrice.carrier.isCarrierOfTheYear === true)) ||
		(selectedServiceLevels.includes("Economy") &&
			carrierPrice.carrier.isEconomy === true) ||
		(selectedServiceLevels.includes("Volume") &&
			carrierPrice.serviceLevelId === 7)
	);
};

export const getUniqueTransitDays = (
	carrierPrices: CarrierPrice[],
): number[] => {
	const uniqueTransitDays = new Set(
		carrierPrices
			.map((carrier) => carrier.transitDays)
			.filter((value) => value !== null),
	);
	return [...uniqueTransitDays].sort((a, b) => Number(a) - Number(b));
};

export const getUniqueServiceTypes = (
	carrierPrices: CarrierPrice[],
): string[] => {
	return carrierPrices
		.map((carrier) => carrier.serviceType)
		.filter((value, index, self) => self.indexOf(value) === index);
};

export const getUniquePrices = (
	carrierPrices: CarrierPrice[],
): (string | number)[] => {
	return carrierPrices
		.map((carrier) => carrier.customerRate)
		.filter((value, index, self) => self.indexOf(value) === index);
};

export const formatCarrierLiability = (carrierPrice: CarrierPrice): string => {
	const newLiability = formatLiabilityValue(carrierPrice.maxLiabilityNew);
	const usedLiability = formatLiabilityValue(carrierPrice.maxLiabilityUsed);
	return `${newLiability} / ${usedLiability}`;
};

export const getUniqueLiabilities = (
	carrierPrices: CarrierPrice[],
): string[] => {
	const uniqueLiabilities = new Map<
		string,
		{ label: string; maxLiabilityNew: number; maxLiabilityUsed: number }
	>();

	for (const carrierPrice of carrierPrices) {
		const maxLiabilityNew = normalizeLiabilityValue(
			carrierPrice.maxLiabilityNew,
		);
		const maxLiabilityUsed = normalizeLiabilityValue(
			carrierPrice.maxLiabilityUsed,
		);
		const key = `${maxLiabilityNew}-${maxLiabilityUsed}`;

		if (!uniqueLiabilities.has(key)) {
			uniqueLiabilities.set(key, {
				label: formatCarrierLiability(carrierPrice),
				maxLiabilityNew,
				maxLiabilityUsed,
			});
		}
	}

	return [...uniqueLiabilities.values()]
		.sort((a, b) => {
			if (a.maxLiabilityNew === b.maxLiabilityNew) {
				return a.maxLiabilityUsed - b.maxLiabilityUsed;
			}
			return a.maxLiabilityNew - b.maxLiabilityNew;
		})
		.map((entry) => entry.label);
};
